import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';

import { config } from '@keytrade/functions';
import { Span } from '@keytrade/components-span';
import { Tabs } from '@keytrade/components-tabs';
import { CardImage } from '@keytrade/components-cardImage';

import Title from '@/components/Title';
import BlockWrapper from '@/components/BlockWrapper';
import Link, { LinkStyle } from '@/components/Link';
import { capitalize } from '@/utils/StringUtils';
import Container from '@/components/Container';
import Paragraph from '@/components/Paragraph';
import { Icon } from '@keytrade/components-icon';
import CurrencyFormatter from '@/formatters/CurrencyFormatter';
import { VisaComparisonTableBlockType } from '@/components/blocks/VisaComparisonTableBlock';
import useTranslations from '@/hooks/useTranslations';

const { list: colors } = config.colors;
const { getFont } = config.fonts;
const InfoBlock = styled(Container)`
  text-align: center;
`;

const Table = styled.table`
  table-layout: fixed;
  width: 100vw;
  flex-shrink: 0;
  border-collapse: collapse;
  scroll-snap-align: start;
`;
const SectionRow = styled.tr`
  border-bottom: 1px solid ${colors.BlueFog};
  > td {
    padding: 4.8rem 0 2rem;
    &:first-child {
      text-align: left;
      padding-left: 2.4rem;
    }
  }
`;
const ValueRow = styled.tr`
  border-bottom: 1px solid ${colors.BlueLight};
  > td {
    padding: 1.6rem;
    &:first-child {
      text-align: left;
      padding-left: 2.4rem;
    }
    &:last-child {
      text-align: right;
      padding-right: 2.4rem;
    }
  }
`;
const Column = styled.td`
  text-align: center;
`;

const ParameterTitle = styled.p`
  color: ${colors.BlueDark};
  font-family: ${({ theme }) => getFont(theme, 'bold')};
  font-size: 16px;
  line-height: 24px;
`;
const ParameterDescription = styled.p`
  color: ${colors.BlueMoon};
  font-family: ${({ theme }) => getFont(theme, 'regular')};
  font-size: 14px;
  line-height: 20px;
`;
const CTAContent = styled.div`
  margin: 0 auto;
`;

const TabsBar = styled.div`
  position: fixed;
  bottom: -100%;
  left: 0;
  width: 100%;
  background: white;
  z-index: 100;
  height: 56px;
  box-shadow: 0px 0px 5px 0px rgba(0, 0, 0, 0.08),
    0px 8px 16px 0px rgba(0, 0, 0, 0.14);
  display: flex;
  justify-content: center;
  align-items: center;
  transition: bottom 1s ease;
  & + div {
    height: 101px;
  }
  &.show {
    bottom: 0;
  }
`;
type SmallProps = {
  margin?: string;
  color?: string;
};
const Small = styled.p<SmallProps>`
  font-size: 14px;
  line-height: 20px;
  ${({ margin }) => margin && `margin: ${margin};`}
  ${({ color }) => color && `color: ${color};`}
`;
const Disclaimers = styled.div`
  padding: 3.2rem 3.2rem 0;
  position: relative;
`;
const BlockWrapperWithGreyTop = styled(BlockWrapper)`
  position: relative;
  z-index: 1;
  top: -5.4rem;
  overflow: hidden;
`;

const ImageContent = styled.div`
  display: inline-block;
`;
type ValueColumnProps = {
  minHeight: number;
};
const ValueColumn = styled.td<ValueColumnProps>`
  ${({ minHeight }) => minHeight && `height: ${minHeight}px;`}
`;

type Props = VisaComparisonTableBlockType;
const VisaComparisonTableBlockMobile: React.FC<Props> = ({
  id,
  title,
  classicPrimaryLink,
  classicSecondaryLink,
  goldPrimaryLink,
  goldSecondaryLink,
  platinumPrimaryLink,
  platinumSecondaryLink,
}) => {
  const { t } = useTranslations('visa_comparison_table');
  const tableRef = useRef<any>();
  const tabs = useRef<Tabs>();
  const tabsBarBottom = useRef<any>();
  const tabsBottom = useRef<any>();
  const [refs, setRefs] = useState([]);
  const addToRefs = (el) => {
    if (el && !refs.map((el) => el.id).includes(el.id)) {
      refs.push(el);
      setRefs(refs);
    }
  };
  const sectionRow = (title) => {
    return (
      <SectionRow>
        <Column colSpan={2}>
          <Title size='sm' level='h3'>
            {title}
          </Title>
        </Column>
      </SectionRow>
    );
  };
  const valueColumn = (
    value,
    disclaimer: number,
    color: string,
    bold: boolean,
    minHeight,
  ) => {
    return (
      <ValueColumn minHeight={minHeight}>
        {value === 'x' && (
          <Icon icon='icn_crossLight' color={colors.BlueCloudy} />
        )}
        {value === 'v' && <Icon icon='icn_check' color={colors.BlueSky} />}
        {value !== 'x' && value !== 'v' && typeof value === 'string' && (
          <Paragraph color='BlueMoon' size='sm'>
            <CurrencyFormatter>
              <Span
                color={color ?? 'BlueMoon'}
                weight={bold ? 'bold' : 'regular'}
              >
                {value}
              </Span>
              {disclaimer === 1 && <sup>*</sup>}
              {disclaimer === 2 && <sup>**</sup>}
            </CurrencyFormatter>
          </Paragraph>
        )}
        {value !== 'x' && value !== 'v' && typeof value !== 'string' && (
          <div>{value}</div>
        )}
      </ValueColumn>
    );
  };
  const valueRow = (
    title,
    description,
    value1,
    disclaimer = null,
    color = null,
    bold = false,
    disclaimerTitle = null,
    minHeight = 60,
  ) => {
    return (
      <ValueRow>
        <Column>
          <ParameterTitle>
            {title}
            {disclaimerTitle === 1 && <sup>*</sup>}
            {disclaimerTitle === 2 && <sup>**</sup>}
          </ParameterTitle>
          {description && (
            <ParameterDescription>{description}</ParameterDescription>
          )}
        </Column>
        {valueColumn(value1, disclaimer, color, bold, minHeight)}
      </ValueRow>
    );
  };

  const TableContent = styled.div`
    display: flex;
    overflow-x: scroll;
    scroll-snap-type: x mandatory;
    align-items: flex-start;
  `;
  const tabNames = [t('classic'), t('gold'), t('platinum')];
  //this needs to be another tag then the one in VisaComparisonTableBlockDesktop else React mixes the classNames with the serverside rendered ones for some reason.
  const MobileBlock = styled.span`
    display: block;
    position: relative;
    &:before {
      content: '';
      position: absolute;
      left: 0;
      right: 0;
      top: 0;
      height: 10rem;
      background-color: ${colors.GreyBackground};
    }
  `;
  const setImages = (precentDiff = 0) => {
    const edge = 40;
    const width = 188;
    const diffDistance = window.innerWidth / 2 - width / 2 + edge;
    const distance = precentDiff * diffDistance;
    const distance2 = (1 - precentDiff) * -diffDistance;
    const distance3 = (precentDiff - 2) * diffDistance;
    refs[0].style.transform = `translateX(${distance}px)`;
    refs[1].style.transform = `translateX(${distance2}px)`;
    refs[2].style.transform = `translateX(${distance3}px)`;
  };
  const onTableScroll = (event) => {
    const scrollIndex = Math.round(event.target.scrollLeft / window.innerWidth);
    if (scrollIndex === event.target.scrollLeft / window.innerWidth) {
      tabs.current.state.activeIndex = scrollIndex;
      tabs.current.forceUpdate();
      tabsBottom.current.state.activeIndex = scrollIndex;
      tabsBottom.current.forceUpdate();
    }
    setImages(event.target.scrollLeft / window.innerWidth);
  };

  const onTabChange = (tabName) => {
    const index = tabNames.indexOf(tabName);

    tableRef.current.scrollTo({
      left: index * window.innerWidth,
      behavior: 'smooth',
    });
  };
  const handleScroll = () => {
    const margin = 150;
    if (tableRef.current.getBoundingClientRect().y < margin) {
      if (
        tableRef.current.getBoundingClientRect().height +
          tableRef.current.getBoundingClientRect().y >
        margin
      )
        tabsBarBottom.current.classList.add('show');
      else {
        tabsBarBottom.current.classList.remove('show');
      }
    } else {
      tabsBarBottom.current.classList.remove('show');
    }
  };
  const handleResize = () => {
    setImages();
  };
  useEffect(() => {
    handleScroll();
    setImages();
    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
    };
  });

  const tableValue = (
    type,
    primaryLink,
    secondaryLink,
    monthly_limit,
    settings_via_app_and_website,
    contactless,
    purchase_insurance,
    safe_online,
    warranty_extension,
    travel_accident,
    travel_assistance,
    travel_cancellation,
    travel_disruption,
    cash_theft_after_a_withdrawal,
    keys_and_papers,
    caretaking_services,
    ticket_cancellation,
  ) => {
    return (
      <Table>
        <tbody>
          <tr>
            <Column colSpan={2}>
              <CTAContent>
                <ImageContent
                  ref={addToRefs}
                  id={type}
                  onClick={() => {
                    onTabChange(t(type));
                  }}
                >
                  <CardImage
                    type={`cardVisa${capitalize(type)}`}
                    imgWidth='180px'
                  />
                </ImageContent>
                <Title size='sm' level='h2' margin='2rem 0 2rem'>
                  {t(`visa_${type}`)}
                </Title>
                <Link
                  {...primaryLink}
                  linkStyle={LinkStyle.PRIMARY}
                  margin='0 0 2rem'
                />
                <br />
                <Link {...secondaryLink} linkStyle={LinkStyle.SECONDARY} />
              </CTAContent>
            </Column>
          </tr>
          {sectionRow(t('main_features'))}
          {valueRow(t('yearly_cost'), null, t('free'), 1, 'GreenSeafoam', true)}
          {valueRow(
            t('max_monthly_limit'),
            null,
            monthly_limit,
            null,
            null,
            false,
            2,
          )}
          {valueRow(
            t('repayment'),
            null,
            t('total_debited_beginning_of_month'),
          )}
          ,
          {valueRow(
            t('settings_via_app_and_website'),
            null,
            settings_via_app_and_website,
          )}
          {valueRow(t('contactless'), null, contactless)}
          {valueRow(
            t('withdraw_in_the_eurozone'),
            null,
            t('free'),
            null,
            'GreenSeafoam',
            true,
          )}
          {sectionRow(t('purchases'))}
          {valueRow(
            t('purchase_insurance'),
            t('per_claim_per_year'),
            purchase_insurance,
          )}
          {valueRow(t('safe_online'), t('per_claim_per_year'), safe_online)}
          {valueRow(t('warranty_extension'), null, warranty_extension)}
          {sectionRow(t('travel_insurances'))}
          {valueRow(t('travel_accident'), null, travel_accident)}
          {valueRow(t('travel_assistance'), null, travel_assistance)}
          {valueRow(
            t('travel_cancellation'),
            t('or_interruption'),
            travel_cancellation,
          )}
          {valueRow(
            t('travel_disruption'),
            t('delayed_or_cancelled_departure'),
            travel_disruption,
          )}
          {sectionRow(t('theft_and_loss_insurances'))}
          {valueRow(
            t('cash_theft_after_a_withdrawal'),
            null,
            cash_theft_after_a_withdrawal,
          )}
          {valueRow(
            t('keys_and_papers'),
            t('per_claim_per_year'),
            keys_and_papers,
          )}
          {sectionRow(t('other_services'))}
          {valueRow(t('caretaking_services'), null, caretaking_services)}
          {valueRow(
            t('ticket_cancellation'),
            null,
            ticket_cancellation,
            null,
            null,
            false,
            null,
            80,
          )}
        </tbody>
      </Table>
    );
  };
  return (
    <MobileBlock>
      <BlockWrapper color='Grey Background' id={id}>
        <InfoBlock narrow>
          <Title size='xxxl' level='h1' margin='-4rem 0 -1.2rem'>
            {title}
          </Title>

          <Tabs
            ref={tabs}
            type='pills'
            onTabChange={onTabChange}
            colorScheme='lightInverted'
            withBorder={false}
            styledComponentsV4
            data={tabNames.map((tab) => {
              return {
                name: tab,
                content: <></>,
              };
            })}
          />
        </InfoBlock>
      </BlockWrapper>
      <BlockWrapperWithGreyTop color='transparent' padding='none'>
        <div>
          <TableContent onScroll={onTableScroll} ref={tableRef}>
            {tableValue(
              'classic',
              classicPrimaryLink,
              classicSecondaryLink,
              '2000EUR',
              'v',
              'v',
              '1500EUR',
              '750EUR',
              'x',
              'x',
              'x',
              'x',
              'x',
              'x',
              'x',
              'x',
              'x',
            )}
            {tableValue(
              'gold',
              goldPrimaryLink,
              goldSecondaryLink,
              '5000EUR',
              'v',
              'v',
              '2000EUR',
              '1000EUR',
              'v',
              '100000EUR',
              'Light service',
              '5000EUR',
              '250EUR',
              '1000EUR',
              'x',
              'x',
              'x',
            )}
            {tableValue(
              'platinum',
              platinumPrimaryLink,
              platinumSecondaryLink,
              '10000EUR',
              'v',
              'v',
              '5000EUR',
              '1500EUR',
              'v',
              '200000EUR',
              'Full service',
              '5000EUR',
              '250EUR',
              '1000EUR',
              '500EUR',
              'v',
              <>
                <Paragraph color='BlueDark'>
                  <CurrencyFormatter>
                    {`250EUR ${t('per_ticket')}`}
                  </CurrencyFormatter>
                </Paragraph>
                <Small color={colors.BlueDark}>
                  <CurrencyFormatter>
                    {`1000EUR ${t('per_year')}`}
                  </CurrencyFormatter>
                </Small>
              </>,
            )}
          </TableContent>

          <Disclaimers>
            <Small color={colors.GreyBlue} margin='0 0 1.6rem'>
              {t('disclaimer1')}
            </Small>
            <Small color={colors.GreyBlue}>{t('disclaimer2')}</Small>
          </Disclaimers>
        </div>
      </BlockWrapperWithGreyTop>
      <TabsBar ref={tabsBarBottom}>
        <Tabs
          ref={tabsBottom}
          type='pills'
          onTabChange={onTabChange}
          colorScheme='light'
          withBorder={false}
          data={tabNames.map((tab) => {
            return {
              name: tab,
              content: <></>,
            };
          })}
        />
      </TabsBar>
    </MobileBlock>
  );
};

export default VisaComparisonTableBlockMobile;
